import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Briefcase, GraduationCap, MessageSquare, Languages, CheckCircle } from 'lucide-react';
import { useCmsContent } from '../hooks/useCmsContent';
import { useLanguage } from '../contexts/LanguageContext';
import { parseHighlight } from '../utils/richText';

const BUSINESS_IMAGE = "/assets/photos/site-updates/business-communication-strategies.webp";
const BUSINESS_IMAGE_PREVIOUS = "/assets/photos/site-updates/classroom-teachers-reference.webp";
const BUSINESS_IMAGE_LEGACY = "https://images.pexels.com/photos/7437492/pexels-photo-7437492.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const GENERAL_IMAGE = "/assets/photos/site-updates/in-loco-training-perfect-one.webp";
const GENERAL_IMAGE_LEGACY = "/assets/photos/site-updates/online-worldwide-working.webp";
const GENERAL_IMAGE_LEGACY_2 = "/assets/photos/site-updates/classroom-group-selfie.webp";
const ACADEMIC_IMAGE = "/assets/photos/site-updates/perfect-academic-writing.webp";
const CONVERSATION_IMAGE = "/assets/photos/site-updates/working.webp";
const CONVERSATION_IMAGE_LEGACY = "/assets/photos/site-updates/group-of-five-alternative.webp";
const PORTUGUESE_IMAGE = "/assets/photos/site-updates/brasil.webp";
const PORTUGUESE_IMAGE_PREVIOUS = "/assets/photos/site-updates/student-results-certificates.webp";
const PORTUGUESE_IMAGE_LEGACY = "/assets/photos/site-updates/flankly.webp";

const DEFAULT_COURSES = [
  {
    icon: BookOpen,
    title: 'General English for Real Life',
    subtitle: 'From Beginners to Advanced',
    description: 'Speak English with confidence wherever life takes you. Our General English classes prepare you for travel, exchange programs, tourism, work opportunities, and building relationships in international and multicultural contexts. Less theory, more real communication. Get the English you actually need to live, connect, and succeed internationally.',
    color: 'bg-[#1A2E6E]',
    badge: 'badge-navy',
    accentBorder: 'border-t-[#1A2E6E]',
    image: GENERAL_IMAGE,
    features: ['Handle airports, hotels, and travel situations with ease', 'Communicate professionally at work and in interviews', 'Connect naturally on social media and in global environments', 'Build meaningful relationships with people from different cultures', 'Make friends and navigate real-life conversations abroad'],
    button: 'Book a Trial Lesson'
  },
  {
    icon: Briefcase,
    title: 'Business English',
    subtitle: 'Professional Communication',
    description: 'Develop the language and communication skills you need for meetings, presentations, interviews, emails, negotiations, and international workplace interactions.',
    color: 'bg-[#F5A623]',
    badge: 'badge-orange',
    accentBorder: 'border-t-[#F5A623]',
    image: BUSINESS_IMAGE,
    features: ['Email writing', 'Presentations', 'Negotiations', 'Interview prep'],
    button: 'Book a Trial Lesson'
  },
  {
    icon: GraduationCap,
    title: 'English for Academic Purposes',
    subtitle: 'Academic Success',
    description: 'Prepare for academic success with lessons designed around essays, lectures, note-taking, academic reading, presentations, and effective study communication in English.',
    color: 'bg-[#4CAF50]',
    badge: 'badge-green',
    accentBorder: 'border-t-[#4CAF50]',
    image: ACADEMIC_IMAGE,
    features: ['Essay writing', 'Note-taking', 'Research skills', 'Academic vocabulary'],
    button: 'Book a Trial Lesson'
  },
  {
    icon: MessageSquare,
    title: 'Conversation Classes',
    subtitle: 'Fluency & Confidence',
    description: 'Practice real speaking with confidence through dynamic discussion-based lessons focused on fluency, pronunciation, spontaneity, and natural communication.',
    color: 'bg-[#1A2E6E]',
    badge: 'badge-navy',
    accentBorder: 'border-t-[#1A2E6E]',
    image: CONVERSATION_IMAGE,
    features: ['Discussion topics', 'Pronunciation', 'Natural flow', 'Confidence building'],
    button: 'Book a Trial Lesson'
  },
  {
    icon: Languages,
    title: 'Brazilian Portuguese',
    subtitle: 'For Native English Speakers',
    description: 'Learn Brazilian Portuguese in a natural, personalized, and confidence-building way with a native teacher who can help you speak clearly and comfortably.',
    color: 'bg-[#4CAF50]',
    badge: 'badge-green',
    accentBorder: 'border-t-[#4CAF50]',
    image: PORTUGUESE_IMAGE,
    features: ['Native teacher', 'Cultural context', 'Practical phrases', 'Personalized pace'],
    button: 'Book a Trial Lesson'
  }
];

export const Courses = () => {
  const cms = useCmsContent('courses');
  const { t, tList } = useLanguage();

  const heroBadge  = cms.get('hero', 'badge',  t('courses.hero.badge'));
  const heroTitle  = cms.get('hero', 'title',  t('courses.hero.title'));
  const heroBody   = cms.get('hero', 'body',   t('courses.hero.body'));
  const ctaTitle   = cms.get('cta', 'title',   t('courses.ctaSection.title'));
  const ctaBody    = cms.get('cta', 'body',    t('courses.ctaSection.body'));
  const ctaPrimary = cms.get('cta', 'cta_primary',   t('courses.ctaSection.primary'));
  const ctaSecondary = cms.get('cta', 'cta_secondary', t('courses.ctaSection.secondary'));

  const localizedCourses = tList('courses.coursesList');
  const cmsCourses = cms.getList('courses', []);
  const courses = DEFAULT_COURSES.map((c, i) => ({
    ...c,
    title:       cmsCourses[i]?.title       || localizedCourses[i]?.title       || c.title,
    subtitle:    cmsCourses[i]?.subtitle    || localizedCourses[i]?.subtitle    || c.subtitle,
    description: cmsCourses[i]?.description || localizedCourses[i]?.description || c.description,
    image:       cmsCourses[i]?.image === GENERAL_IMAGE_LEGACY || cmsCourses[i]?.image === GENERAL_IMAGE_LEGACY_2
      ? GENERAL_IMAGE
      : ([BUSINESS_IMAGE_LEGACY, BUSINESS_IMAGE_PREVIOUS].includes(cmsCourses[i]?.image)
          ? BUSINESS_IMAGE
          : (cmsCourses[i]?.image === CONVERSATION_IMAGE_LEGACY
              ? CONVERSATION_IMAGE
              : ([PORTUGUESE_IMAGE_LEGACY, PORTUGUESE_IMAGE_PREVIOUS].includes(cmsCourses[i]?.image) ? PORTUGUESE_IMAGE : (cmsCourses[i]?.image || c.image)))),
    button:      cmsCourses[i]?.button      || localizedCourses[i]?.button      || c.button,
    features:    cmsCourses[i]?.features
      ? (typeof cmsCourses[i].features === 'string'
          ? cmsCourses[i].features.split('\n').filter(Boolean)
          : cmsCourses[i].features)
      : (localizedCourses[i]?.features || c.features),
  }));

  return (
    <main className="pt-24" data-testid="courses-page">
      {/* Hero Section */}
      <section className="section-padding bg-cream-gradient">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="badge-navy mb-4 inline-block">
              {heroBadge}
            </span>
            <h1 className="font-sora text-4xl md:text-5xl font-bold text-[#1A2E6E] mb-6">
              {parseHighlight(heroTitle)}
            </h1>
            <p className="text-lg text-[#1A2E6E]/70 leading-relaxed">
              {heroBody}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="section-padding bg-warm-texture">
        <div className="container-custom">
          <div className="space-y-14 md:space-y-16">
            {courses.map((course, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <span className={`${course.badge} text-xs mb-3 inline-flex items-center gap-2`}>
                    <course.icon size={14} />
                    {course.subtitle}
                  </span>
                  <h2 className="font-sora text-3xl font-bold text-[#1A2E6E] mb-4">
                    {course.title}
                  </h2>
                  <p className="text-[#1A2E6E]/70 leading-relaxed mb-6">
                    {course.description}
                  </p>
                  <div className="grid grid-cols-1 gap-3 mb-8 sm:grid-cols-2">
                    {course.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle size={16} className="mt-0.5 flex-shrink-0 text-[#4CAF50]" />
                        <span className="text-sm text-[#1A2E6E]/70">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/book-trial"
                    className="btn-primary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                    data-testid={`course-${index}-book-trial`}
                  >
                    {course.button} <ArrowRight size={18} />
                  </Link>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className={`glass-card p-4 border-t-4 ${course.accentBorder}`}>
                    <div className="photo-container h-56 sm:h-64">
                      <img
                        src={course.image}
                        alt={course.title}
                        loading="lazy"
                        className="photo-fit-card"
                      />
                    </div>
                    <div className="mt-4 flex items-center justify-between px-2">
                      <div>
                        <p className="text-xs text-[#1A2E6E]/50">{t('courses.personalizedFor')}</p>
                        <p className="font-semibold text-[#1A2E6E]">{t('courses.yourGoals')}</p>
                      </div>
                      <span className={`${course.badge} text-xs`}>
                        {t('courses.availableNow')}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-paper-lines">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 sm:p-10 lg:p-12 rounded-3xl text-center max-w-3xl mx-auto border-t-4 border-t-[#D72B2B]"
          >
            <h2 className="font-sora text-3xl font-bold text-[#1A2E6E] mb-4">
              {parseHighlight(ctaTitle)}
            </h2>
            <p className="text-[#1A2E6E]/70 mb-8">
              {ctaBody}
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row sm:flex-wrap">
              <Link
                to="/level-test"
                className="btn-primary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                data-testid="courses-level-test-btn"
              >
                {ctaPrimary} <ArrowRight size={18} />
              </Link>
              <Link
                to="/book-trial"
                className="btn-secondary w-full text-center sm:w-auto"
                data-testid="courses-book-trial-btn"
              >
                {ctaSecondary}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
